import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Slide,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import CheckIcon from "@material-ui/icons/Check";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { apiPath } from "api";
import {
  generalAuthTokenHeader,
  multimediaAuthTokenHeader,
} from "__helpers/auth-header";
import { userService } from "_services/user.service";
import moment from "moment";

import "./CreatePatientModal.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

class CreatePatientModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "upload", // "upload" or "manual"

      // Manual form fields
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: null,
      estimateDischargeDate: null,
      insurance: "",
      insurancePolicyNum: "",
      referringFacility: "",
      address: "",
      city: "",
      zip: "",
      phone: "",
      primaryDiagnosis: "",
      referringPhysicianNpi: "",
      patientNote: "",

      // Upload fields
      uploadedFiles: [],
      dragActive: false,

      // Form state
      loading: false,
      errors: {},

      // Copy to clipboard state
      copiedField: null,
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCopyToClipboard = this.handleCopyToClipboard.bind(this);
    this.renderCopyButton = this.renderCopyButton.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.removeFile = this.removeFile.bind(this);
    this.resetForm = this.resetForm.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.resetForm();
    }
  }

  resetForm() {
    this.setState({
      activeTab: "upload",
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: null,
      estimateDischargeDate: null,
      insurance: "",
      insurancePolicyNum: "",
      referringFacility: "",
      address: "",
      city: "",
      zip: "",
      phone: "",
      primaryDiagnosis: "",
      referringPhysicianNpi: "",
      patientNote: "",
      uploadedFiles: [],
      loading: false,
      errors: {},
    });
  }

  handleTabChange(tab) {
    this.setState({ activeTab: tab, errors: {} });
  }

  handleInputChange(event) {
    const { name, value } = event.target;
    this.setState({
      [name]: value,
      errors: { ...this.state.errors, [name]: "" },
    });
  }

  handleDateChange(name, date) {
    this.setState({
      [name]: date,
      errors: { ...this.state.errors, [name]: "" },
    });
  }

  handleCopyToClipboard(fieldName, value) {
    if (!value) return;

    const stringValue = typeof value === 'object' && value instanceof Date
      ? value.toLocaleDateString('en-US')
      : String(value);

    navigator.clipboard.writeText(stringValue).then(() => {
      this.setState({ copiedField: fieldName });
      setTimeout(() => {
        this.setState((prevState) => {
          if (prevState.copiedField === fieldName) {
            return { copiedField: null };
          }
          return null;
        });
      }, 1500);
    });
  }

  renderCopyButton(fieldName, value) {
    const { copiedField } = this.state;
    const isCopied = copiedField === fieldName;

    return (
      <IconButton
        className={`copy-btn ${isCopied ? 'copied' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          this.handleCopyToClipboard(fieldName, value);
        }}
        size="small"
        disabled={!value}
        title={isCopied ? "Copied!" : "Copy to clipboard"}
      >
        {isCopied ? <CheckIcon fontSize="small" /> : <FileCopyOutlinedIcon fontSize="small" />}
      </IconButton>
    );
  }

  handleDrag(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      this.setState({ dragActive: true });
    } else if (e.type === "dragleave") {
      this.setState({ dragActive: false });
    }
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragActive: false });

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      this.processFiles(e.dataTransfer.files);
    }
  }

  handleFileUpload(e) {
    if (e.target.files && e.target.files.length > 0) {
      this.processFiles(e.target.files);
    }
  }

  processFiles(fileList) {
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const newFiles = [];
    const errors = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];

      if (!allowedTypes.includes(file.type)) {
        errors.push(
          `${file.name}: Invalid file type. Only JPG, PNG, or PDF allowed.`
        );
        continue;
      }

      if (file.size > maxSize) {
        errors.push(`${file.name}: File size exceeds 10MB limit.`);
        continue;
      }

      newFiles.push(file);
    }

    if (errors.length > 0) {
      this.setState({
        errors: { ...this.state.errors, files: errors.join(" ") },
      });
    }

    if (newFiles.length > 0) {
      this.setState((prevState) => ({
        uploadedFiles: [...prevState.uploadedFiles, ...newFiles],
        errors: { ...prevState.errors, files: "" },
      }));
    }
  }

  removeFile(index) {
    this.setState((prevState) => ({
      uploadedFiles: prevState.uploadedFiles.filter((_, i) => i !== index),
    }));
  }

  validateManualForm() {
    const errors = {};
    const { firstName, lastName, gender, dateOfBirth } = this.state;

    if (!firstName.trim()) errors.firstName = "First name is required";
    if (!lastName.trim()) errors.lastName = "Last name is required";
    if (!gender) errors.gender = "Gender is required";
    if (!dateOfBirth) errors.dateOfBirth = "Date of birth is required";

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  validateUploadForm() {
    const errors = {};
    const { uploadedFiles } = this.state;

    if (uploadedFiles.length === 0) {
      errors.files = "Please upload at least one document";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  async handleSubmit(e) {
    e.preventDefault();

    const { activeTab } = this.state;

    if (activeTab === "manual") {
      if (!this.validateManualForm()) return;
      await this.submitManualPatient();
    } else {
      if (!this.validateUploadForm()) return;
      await this.submitAiGeneratedPatient();
    }
  }

  async submitManualPatient() {
    this.setState({ loading: true });

    try {
      const {
        firstName,
        lastName,
        gender,
        dateOfBirth,
        estimateDischargeDate,
        insurance,
        insurancePolicyNum,
        referringFacility,
        address,
        city,
        zip,
        phone,
        primaryDiagnosis,
        referringPhysicianNpi,
        patientNote,
      } = this.state;

      const requestBody = {
        firstName,
        lastName,
        gender: gender || null,
        dateOfBirth: dateOfBirth
          ? moment(dateOfBirth).format("YYYY-MM-DD")
          : null,
        estimateDischargeDate: estimateDischargeDate
          ? moment(estimateDischargeDate).format("YYYY-MM-DD")
          : null,
        insurance: insurance || null,
        insurancePolicyNum: insurancePolicyNum || null,
        referringFacility: referringFacility || null,
        address: address || null,
        city: city || null,
        zip: zip || null,
        phone: phone || null,
        primaryDiagnosis: primaryDiagnosis || null,
        referringPhysicianNpi: referringPhysicianNpi || null,
        patientNote: patientNote || null,
      };

      const response = await fetch(apiPath.externalPatientsManual, {
        method: "POST",
        headers: generalAuthTokenHeader(),
        body: JSON.stringify(requestBody),
      });

      if (response.ok || response.status === 201) {
        const data = await response.json();
        userService.showNotification({
          title: "Success",
          message: "Patient created successfully",
          type: "success",
        });
        this.props.onPatientCreated(data);
      } else {
        const errorData = await response.json();
        userService.showNotification({
          title: "Error",
          message: errorData.message || "Failed to create patient",
          type: "danger",
        });
        this.setState({
          errors: { submit: errorData.message || "Failed to create patient" },
        });
      }
    } catch (error) {
      console.error("Error creating patient:", error);
      userService.showNotification({
        title: "Error",
        message: "An error occurred while creating the patient",
        type: "danger",
      });
      this.setState({
        errors: { submit: "An error occurred while creating the patient" },
      });
    } finally {
      this.setState({ loading: false });
    }
  }

  async submitAiGeneratedPatient() {
    this.setState({ loading: true });

    try {
      const { uploadedFiles } = this.state;

      const formData = new FormData();
      uploadedFiles.forEach((file) => {
        formData.append("files", file);
      });

      const headers = multimediaAuthTokenHeader();
      delete headers["Content-Type"]; // Let browser set content type for FormData

      const response = await fetch(apiPath.externalPatientsAiGenerated, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      if (response.ok || response.status === 202) {
        const data = await response.json();
        userService.showNotification({
          title: "Success",
          message: "Documents uploaded. AI is processing...",
          type: "success",
        });
        this.props.onPatientCreated(data);
      } else {
        const errorData = await response.json();
        userService.showNotification({
          title: "Error",
          message: errorData.message || "Failed to upload documents",
          type: "danger",
        });
        this.setState({
          errors: { submit: errorData.message || "Failed to upload documents" },
        });
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      userService.showNotification({
        title: "Error",
        message: "An error occurred while uploading documents",
        type: "danger",
      });
      this.setState({
        errors: { submit: "An error occurred while uploading documents" },
      });
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const { open, onClose } = this.props;
    const {
      activeTab,
      firstName,
      lastName,
      gender,
      dateOfBirth,
      estimateDischargeDate,
      insurance,
      insurancePolicyNum,
      referringFacility,
      address,
      city,
      zip,
      phone,
      primaryDiagnosis,
      referringPhysicianNpi,
      uploadedFiles,
      dragActive,
      loading,
      errors,
    } = this.state;

    return (
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={onClose}
        TransitionComponent={Transition}
        className="create-patient-modal"
      >
        <DialogTitle className="modal-title">
          Create Intake
          <IconButton className="close-button" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modal-content">
          {/* Tab Buttons */}
          <Box className="tab-buttons">
            <Button
              className={`tab-btn ${
                activeTab === "upload" ? "active" : ""
              } text-white`}
              onClick={() => this.handleTabChange("upload")}
            >
              Upload Patient Document
            </Button>
            <Button
              className={`tab-btn ${
                activeTab === "manual" ? "active" : ""
              } text-white`}
              onClick={() => this.handleTabChange("manual")}
            >
              Enter Manually
            </Button>
          </Box>

          {activeTab === "manual" ? (
            /* Manual Entry Form */
            <form className="manual-form">
              <Box display="flex" flexWrap="wrap" className="form-row">
                {/* First Name */}
                <Box className="form-field half">
                  <label>First Name <span style={{ color: 'red' }}>*</span></label>
                  <div className="field-with-copy">
                    <TextField
                      name="firstName"
                      value={firstName}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      placeholder="Enter first name"
                    />
                    {this.renderCopyButton('firstName', firstName)}
                  </div>
                </Box>

                {/* Last Name */}
                <Box className="form-field half">
                  <label>Last Name <span style={{ color: 'red' }}>*</span></label>
                  <div className="field-with-copy">
                    <TextField
                      name="lastName"
                      value={lastName}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      placeholder="Enter last name"
                    />
                    {this.renderCopyButton('lastName', lastName)}
                  </div>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" className="form-row">
                {/* Gender */}
                <Box className="form-field half">
                  <label>Gender <span style={{ color: 'red' }}>*</span></label>
                  <div className="field-with-copy">
                    <FormControl variant="outlined" fullWidth error={!!errors.gender}>
                      <Select
                        name="gender"
                        value={gender}
                        onChange={this.handleInputChange}
                        displayEmpty
                      >
                        <MenuItem value="">Select Gender</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Others">Others</MenuItem>
                      </Select>
                      {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                    </FormControl>
                    {this.renderCopyButton('gender', gender)}
                  </div>
                </Box>

                {/* Date of Birth */}
                <Box className="form-field half">
                  <label>Date of Birth <span style={{ color: 'red' }}>*</span></label>
                  <div className="field-with-copy">
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        format="MM/dd/yyyy"
                        value={dateOfBirth}
                        onChange={(date) =>
                          this.handleDateChange("dateOfBirth", date)
                        }
                        inputVariant="outlined"
                        fullWidth
                        placeholder="MM/DD/YYYY"
                        error={!!errors.dateOfBirth}
                        helperText={errors.dateOfBirth}
                        KeyboardButtonProps={{ "aria-label": "change date" }}
                      />
                    </MuiPickersUtilsProvider>
                    {this.renderCopyButton('dateOfBirth', dateOfBirth)}
                  </div>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" className="form-row">
                {/* Address */}
                <Box className="form-field half">
                  <label>Address</label>
                  <div className="field-with-copy">
                    <TextField
                      name="address"
                      value={address}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter street address"
                    />
                    {this.renderCopyButton('address', address)}
                  </div>
                </Box>

                {/* Phone */}
                <Box className="form-field half">
                  <label>Phone</label>
                  <div className="field-with-copy">
                    <TextField
                      name="phone"
                      value={phone}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter phone number"
                    />
                    {this.renderCopyButton('phone', phone)}
                  </div>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" className="form-row">
                {/* City */}
                <Box className="form-field half">
                  <label>City</label>
                  <div className="field-with-copy">
                    <TextField
                      name="city"
                      value={city}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter city"
                    />
                    {this.renderCopyButton('city', city)}
                  </div>
                </Box>

                {/* Zip */}
                <Box className="form-field half">
                  <label>Zip Code</label>
                  <div className="field-with-copy">
                    <TextField
                      name="zip"
                      value={zip}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter zip code"
                    />
                    {this.renderCopyButton('zip', zip)}
                  </div>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" className="form-row">
                {/* Insurance */}
                <Box className="form-field half">
                  <label>Insurance</label>
                  <div className="field-with-copy">
                    <TextField
                      name="insurance"
                      value={insurance}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter insurance name"
                    />
                    {this.renderCopyButton('insurance', insurance)}
                  </div>
                </Box>

                {/* Insurance Policy Number */}
                <Box className="form-field half">
                  <label>Insurance Policy Number</label>
                  <div className="field-with-copy">
                    <TextField
                      name="insurancePolicyNum"
                      value={insurancePolicyNum}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter policy number"
                    />
                    {this.renderCopyButton('insurancePolicyNum', insurancePolicyNum)}
                  </div>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" className="form-row">
                {/* Primary Diagnosis */}
                <Box className="form-field half">
                  <label>Primary Diagnosis</label>
                  <div className="field-with-copy">
                    <TextField
                      name="primaryDiagnosis"
                      value={primaryDiagnosis}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter primary diagnosis"
                    />
                    {this.renderCopyButton('primaryDiagnosis', primaryDiagnosis)}
                  </div>
                </Box>

                {/* Estimated Discharge Date */}
                <Box className="form-field half">
                  <label>Estimated Discharge Date</label>
                  <div className="field-with-copy">
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        format="MM/dd/yyyy"
                        value={estimateDischargeDate}
                        onChange={(date) =>
                          this.handleDateChange("estimateDischargeDate", date)
                        }
                        inputVariant="outlined"
                        fullWidth
                        placeholder="MM/DD/YYYY"
                        KeyboardButtonProps={{ "aria-label": "change date" }}
                      />
                    </MuiPickersUtilsProvider>
                    {this.renderCopyButton('estimateDischargeDate', estimateDischargeDate)}
                  </div>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" className="form-row">
                {/* Referring Facility */}
                <Box className="form-field half">
                  <label>Referring Facility</label>
                  <div className="field-with-copy">
                    <TextField
                      name="referringFacility"
                      value={referringFacility}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter referring facility"
                    />
                    {this.renderCopyButton('referringFacility', referringFacility)}
                  </div>
                </Box>

                {/* Referring Physician NPI */}
                <Box className="form-field half">
                  <label>Referring Physician NPI</label>
                  <div className="field-with-copy">
                    <TextField
                      name="referringPhysicianNpi"
                      value={referringPhysicianNpi}
                      onChange={this.handleInputChange}
                      variant="outlined"
                      fullWidth
                      placeholder="Enter NPI number"
                    />
                    {this.renderCopyButton('referringPhysicianNpi', referringPhysicianNpi)}
                  </div>
                </Box>
              </Box>

              {errors.submit && (
                <Box className="error-message">{errors.submit}</Box>
              )}
            </form>
          ) : (
            /* Upload Document Form */
            <div className="upload-form">
              <div
                className={`upload-dropzone ${dragActive ? "drag-active" : ""}`}
                onDragEnter={this.handleDrag}
                onDragLeave={this.handleDrag}
                onDragOver={this.handleDrag}
                onDrop={this.handleDrop}
              >
                <input
                  type="file"
                  id="file-upload"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={this.handleFileUpload}
                  style={{ display: "none" }}
                />
                <label htmlFor="file-upload" className="dropzone-label">
                  <CloudUploadIcon className="upload-icon" />
                  <p className="upload-text">
                    Select a file or drag and drop here
                  </p>
                  <p className="upload-hint">
                    JPG, PNG or PDF, file size no more than 10MB
                  </p>
                </label>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files-list">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="uploaded-file-item">
                      <span className="file-name">{file.name}</span>
                      <IconButton
                        className="delete-file-btn"
                        onClick={() => this.removeFile(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}

              {errors.files && (
                <Box className="error-message">{errors.files}</Box>
              )}

              {errors.submit && (
                <Box className="error-message">{errors.submit}</Box>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions
          className="modal-actions"
          style={{ justifyContent: "flex-end" }}
        >
          <Button onClick={onClose} className="cancel-btn" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={this.handleSubmit}
            className="submit-btn"
            disabled={loading}
          >
            {loading && <CircularProgress size={20} className="btn-loader" />}
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default CreatePatientModal;
